// UsageChart — stacked monthly bars, per instance.
//
// Resolves the `TODO(charts)` the 2026-08-26 design drop shipped this file
// with. The drop drew the bars as hand-positioned `<rect>` elements against a
// fixed 640-unit viewBox, which was the right thing for a review prototype and
// the wrong thing for live data: the geometry assumed six months and three
// series, the y-axis ceiling was rounded to the nearest thousand (so a fleet
// metering tens of units drew one flat row of bars at the bottom), and there was
// no tooltip, so a bar was a shape with no readable value.
//
// This is recharts, which the app already depends on, driven by whatever months
// and series it is handed. The design's forms are kept: the same stacked bars,
// the same two-tick dashed grid, the same abbreviated y-axis labels, and the
// same swatch legend underneath.
//
// ADAPTED (colour): the drop hardcodes `["var(--miq-primary)", "#7DD3FC",
// "#94A3B8"]` — two literal hex values, one of which was at the time the design
// system's own dark chart tail (that token has since been renamed
// `--miq-nav-purpose-dark`, for the nav accent it actually represents). The DS
// publishes a five-slot categorical palette as `--chart-1` … `--chart-5`,
// re-pointed per surface so the dark theme gets its own values, and this uses
// that instead. A hardcoded hex is how two metricsIQ surfaces drift apart, and
// in a chart it is also how a series becomes invisible against a dark
// background.
//
// ADAPTED (not the DS chart components): the design system ships a `chart`
// module wrapping recharts, and it cannot be imported here — it names
// `recharts@2.15.2` as a version-pinned specifier that no bundler resolves.
// AppShell.tsx explains the same constraint for the barrel. recharts is imported
// directly, which is what the DS wrapper would have done.
//
// FIXTURE DEFAULTS REMOVED (2026-09-01). This file used to default `months`
// and `series` to the design drop's numbers so that "the screens a later phase
// owns" could render before they had data. Home was the one screen relying on
// it, and the later phase arrived without anyone revisiting the call: Home
// called `<UsageStackedBars height={190} />` with no data, so a LIVE CUSTOMER
// WITH 1,227 METERED HOURS was shown the drop's invented ~30k/month across
// three instances it does not have, captioned only by a line of small grey
// text. It read as their usage because everything about it — real month
// labels, a plausible axis, a legend — was built to.
//
// `months` and `series` are therefore REQUIRED now. A caller with no data to
// pass cannot silently get somebody else's: it fails to compile. An empty
// `series` is still a perfectly good argument and renders the empty state
// below, which is what a screen with nothing to show actually wants.

import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ABSENT, quantityAsPixelValue, quantityText } from '../../data/format';

/**
 * One instance's bar in every month, in axis order.
 *
 * Quantities are the ledger's exact decimal strings, and `null` where the
 * instance recorded nothing that month — which stacks as nothing rather than
 * shifting the bars along the axis.
 */
export interface UsageChartSeries {
  name: string;
  quantities: (string | null)[];
}

/**
 * The design system's categorical palette, cycled.
 *
 * Five hues — not five steps of one hue, which is what these tokens used to
 * hold and why two instances once drew as the same blue. Order matters: the
 * slots are arranged so that neighbours stay apart under red-green colour
 * vision deficiency, so a series takes the next slot and never a colour
 * chosen per-chart.
 *
 * A sixth series reuses the first. A fleet large enough to run out of colours
 * needs a different chart, not a sixth colour nobody chose.
 */
const SERIES_COLOURS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

/** One month's bar, as recharts wants it: a label and one key per series. */
type ChartRow = { month: string } & Record<string, number | string>;

export interface UsageStackedBarsProps {
  height?: number;
  /** Axis labels, oldest first. Already formatted — the caller owns the words. */
  months: string[];
  /** One entry per instance. Empty is valid and renders the empty state. */
  series: UsageChartSeries[];
}

export function UsageStackedBars({
  height = 220,
  months,
  series,
}: UsageStackedBarsProps) {
  // An empty chart is a state, not a failure: an account whose instances have
  // never reported has an axis and no bars, and saying so is more use than six
  // empty gridlines.
  if (series.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height }}
      >
        No usage recorded in these months yet.
      </div>
    );
  }

  const rows: ChartRow[] = months.map((month, index) => {
    const row: ChartRow = { month };
    for (const instance of series) {
      row[instance.name] = quantityAsPixelValue(instance.quantities[index] ?? null);
      // The exact figure travels alongside its pixel value so the tooltip can
      // show what the ledger holds rather than what the geometry rounded to.
      row[exactKey(instance.name)] = instance.quantities[index] ?? ABSENT;
    }
    return row;
  });

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
          {/* Horizontal only, dashed, as the prototype drew it: the bars carry
              the categories, so vertical rules would be decoration. */}
          <CartesianGrid
            horizontal
            vertical={false}
            strokeDasharray="3 4"
            stroke="var(--color-border)"
          />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={52}
            tickFormatter={abbreviate}
            tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10.5 }}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-accent)', opacity: 0.4 }}
            content={<ExactTooltip series={series} />}
          />
          {series.map((instance, index) => (
            <Bar
              key={instance.name}
              dataKey={instance.name}
              stackId="usage"
              fill={SERIES_COLOURS[index % SERIES_COLOURS.length]}
              radius={index === series.length - 1 ? [2, 2, 0, 0] : 0}
              maxBarSize={46}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
        {series.map((instance, index) => (
          <span key={instance.name} className="flex items-center gap-1.5">
            <span
              className="size-2.5 rounded-sm"
              style={{ background: SERIES_COLOURS[index % SERIES_COLOURS.length] }}
            />
            {instance.name}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The hovered month, with every series' exact quantity.
 *
 * A custom panel rather than recharts' default because the default prints the
 * numeric value it used for the geometry, which is the one figure on this page
 * that must not be shown.
 */
function ExactTooltip({
  active,
  payload,
  label,
  series,
}: {
  active?: boolean;
  payload?: { payload?: ChartRow }[];
  label?: string;
  series: UsageChartSeries[];
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) {
    return null;
  }

  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-semibold text-popover-foreground">{label}</div>
      {series.map((instance, index) => (
        <div key={instance.name} className="flex items-center gap-2">
          <span
            className="size-2 rounded-sm"
            style={{ background: SERIES_COLOURS[index % SERIES_COLOURS.length] }}
          />
          <span className="text-muted-foreground">{instance.name}</span>
          <span className="ml-auto font-medium text-popover-foreground">
            {quantityText(exactOf(row, instance.name))}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Where a series' exact decimal string rides on the row. */
function exactKey(name: string): string {
  return `${name}\u0000exact`;
}

function exactOf(row: ChartRow, name: string): string | null {
  const exact = row[exactKey(name)];
  return typeof exact === 'string' && exact !== ABSENT ? exact : null;
}

/** `36000` reads as `36k`. The prototype's y-axis, kept. */
function abbreviate(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return `${trim(value / 1_000_000)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${trim(value / 1_000)}k`;
  }
  return trim(value);
}

function trim(value: number): string {
  return String(Math.round(value * 10) / 10);
}
